// Transaction document summaries (by item / by client), exported as an Xtram PDF or Excel report.
import * as XLSX from 'xlsx-js-style';
import { applyWorkbookLtr, buildReportWorkbook } from './ExcelReport';
import { downloadBlob } from './blobDownload';
import EnumsService from '../Services/enums.service';
import { XTRAM_LOGOS } from '../assests/branding/xtram/logos';
import { AGING_REPORT_COLORS as C } from './receivablesAgingExport';

const escapeHtml = (value) =>
    String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

const safeFileName = (value) => String(value || 'Client').replace(/[\\/:*?"<>|]/g, '_');

const formatAmount = (value, decimals = 2) => {
    const parsed = Number(value ?? 0);
    return Number.isNaN(parsed)
        ? '0.00'
        : parsed.toLocaleString('he-IL', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const fileBase = ({ clientName, clientCase, fileSuffix }) =>
    `${safeFileName(clientName)}-${clientCase || ''}_${fileSuffix}`;

const headerTitleLines = ({ clientName, clientCase, title, subtitle }) =>
    [`${clientCase || ''}    ${clientName || ''}`.trim(), title, subtitle].filter(Boolean);

const joinPairs = (pairs) => (pairs || []).map((pair) => `${pair.label}: ${pair.value}`).join('   ·   ');

/**
 * @param {Object}   report
 * @param {string}   report.title       Report title (e.g. "Sales by item").
 * @param {string}   [report.subtitle]  Period and active filters.
 * @param {{label: string, value: string}[]} [report.summaryItems]  Headline totals shown above the table.
 * @param {{label: string, value: string}[]} [report.typeBreakdown] Count and total per document type.
 * @param {{label: string, numeric?: boolean, decimals?: number, width?: number}[]} report.columns
 * @param {Array<Array<*>>} report.rows Data rows aligned with `columns`.
 * @param {Array<*>} [report.totalRow]
 */
export const downloadTransactionSummaryExcel = ({ report, clientName, clientCase, fileSuffix, direction }) => {
    const titleLines = [
        ...headerTitleLines({ clientName, clientCase, title: report.title, subtitle: report.subtitle }),
        joinPairs(report.summaryItems),
        joinPairs(report.typeBreakdown),
    ].filter(Boolean);
    const rows = report.totalRow ? [...report.rows, report.totalRow] : report.rows;
    const wb = buildReportWorkbook({
        sheetName: String(report.title || 'Summary').slice(0, 31),
        titleLines,
        header: report.columns.map((col) => col.label),
        rows,
        colWidths: report.columns.map((col) => col.width ?? (col.numeric ? 16 : 24)),
        numericColIndexes: report.columns.map((col, i) => (col.numeric ? i : -1)).filter((i) => i >= 0),
        totalRowIndexes: report.totalRow ? [rows.length - 1] : [],
    });
    if (direction === 'ltr') applyWorkbookLtr(wb);
    XLSX.writeFile(wb, `${fileBase({ clientName, clientCase, fileSuffix })}.xlsx`);
};

const assetToDataUrl = async (assetUrl) => {
    try {
        const res = await fetch(assetUrl);
        if (!res.ok) return '';
        const blob = await res.blob();
        return await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(String(reader.result || ''));
            reader.onerror = () => resolve('');
            reader.readAsDataURL(blob);
        });
    } catch {
        return '';
    }
};

const buildPdfHtml = ({ report, clientName, clientCase, logoDataUrl, direction }) => {
    const dir = direction === 'ltr' ? 'ltr' : 'rtl';
    const textAlign = dir === 'rtl' ? 'right' : 'left';
    const cell = (value, col) => {
        if (col.numeric) {
            const negative = Number(value) < 0;
            return `<td class="num${negative ? ' negative' : ''}">${escapeHtml(formatAmount(value, col.decimals ?? 2))}</td>`;
        }
        return `<td class="txt">${escapeHtml(value)}</td>`;
    };
    const bodyHtml = report.rows
        .map((row, idx) => `<tr class="${idx % 2 === 1 ? 'stripe' : 'plain'}">${row
            .map((value, i) => cell(value, report.columns[i]))
            .join('')}</tr>`)
        .join('');
    const totalHtml = report.totalRow
        ? `<tr class="total">${report.totalRow
            .map((value, i) => (value === '' || value == null
                ? '<td></td>'
                : cell(value, report.columns[i])))
            .join('')}</tr>`
        : '';
    const titles = headerTitleLines({ clientName, clientCase, title: report.title, subtitle: report.subtitle });
    const summaryHtml = report.summaryItems?.length
        ? `<div class="summary">${report.summaryItems
            .map((item) => `<div class="summary-box"><div class="label">${escapeHtml(item.label)}</div><div class="value">${escapeHtml(item.value)}</div></div>`)
            .join('')}</div>`
        : '';
    const now = new Date();
    const printedAt = `${now.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })} ${now.toLocaleDateString('he-IL')}`;

    return `<!DOCTYPE html>
<html lang="${dir === 'rtl' ? 'he' : 'en'}" dir="${dir}">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(report.title)}</title>
  <style>
    @page { size: A4 ${report.columns.length > 7 ? 'landscape' : 'portrait'}; margin: 10mm 8mm; }
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, "Segoe UI", sans-serif; direction: ${dir}; color: ${C.text}; margin: 0; padding: 6px;
      -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .top-bar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; font-size: 11px; }
    .top-bar img { height: 28px; }
    .center-block { text-align: center; flex: 1; }
    h1 { margin: 2px 0 4px; font-size: 18px; color: ${C.primary}; font-weight: 700; }
    .meta { font-size: 11px; font-weight: 600; }
    table { width: 100%; border-collapse: collapse; font-size: 10px; }
    thead { display: table-header-group; }
    tr { page-break-inside: avoid; }
    th, td { border: 1px solid ${C.border}; padding: 5px 4px; text-align: center; vertical-align: top; }
    th { background: ${C.primary} !important; color: ${C.headerFont} !important; font-weight: 700; }
    td.txt { text-align: ${textAlign}; }
    td.num { white-space: nowrap; font-variant-numeric: tabular-nums; font-weight: 600; }
    td.negative { color: #c0392b; }
    tr.plain td { background: ${C.plain} !important; }
    tr.stripe td { background: ${C.stripe} !important; }
    tr.total td { font-weight: 700; background: ${C.totalFill} !important; color: ${C.primaryDark} !important; }
    .summary { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px; }
    .summary-box { flex: 1 1 0; min-width: 90px; border: 1px solid ${C.border}; border-radius: 6px; padding: 5px 8px; background: ${C.stripe}; }
    .summary-box .label { font-size: 9px; color: #687991; font-weight: 600; }
    .summary-box .value { font-size: 12px; font-weight: 700; color: ${C.primaryDark}; direction: ltr; text-align: ${textAlign}; }
    .types { font-size: 10px; font-weight: 600; margin-bottom: 8px; color: ${C.text}; }
  </style>
</head>
<body>
  <div class="top-bar">
    <div>${logoDataUrl ? `<img src="${logoDataUrl}" alt="Xtram" />` : ''}</div>
    <div class="center-block">
      <div class="meta">${escapeHtml(titles[0] || '')}</div>
      <h1>${escapeHtml(report.title)}</h1>
      <div class="meta">${escapeHtml(report.subtitle || '')}</div>
    </div>
    <div>${escapeHtml(printedAt)}</div>
  </div>
  ${summaryHtml}
  ${report.typeBreakdown?.length ? `<div class="types">${escapeHtml(joinPairs(report.typeBreakdown))}</div>` : ''}
  <table>
    <thead><tr>${report.columns.map((col) => `<th>${escapeHtml(col.label)}</th>`).join('')}</tr></thead>
    <tbody>${bodyHtml}${totalHtml}</tbody>
  </table>
</body>
</html>`;
};

export const downloadTransactionSummaryPdf = async ({ report, clientName, clientCase, fileSuffix, direction, token }) => {
    if (!token) throw new Error('Missing auth token');
    if (!clientCase) throw new Error('Missing case id');

    const logoDataUrl = await assetToDataUrl(XTRAM_LOGOS.wordmarkColorPng);
    const html = buildPdfHtml({ report, clientName, clientCase, logoDataUrl, direction });
    const res = await EnumsService.GeneratePdfFromHtml(html, clientCase, token);
    if (!res?.ok) {
        const errText = await res?.text?.().catch(() => '');
        throw new Error(errText || 'PDF generation failed');
    }

    let blob;
    if ((res.headers.get('content-type') || '').includes('application/json')) {
        const json = await res.json();
        const pdfBase64 = json.data?.PdfBase64 || json.data?.pdfBase64 || json.PdfBase64;
        if (!pdfBase64) throw new Error('No PDF base64 in response');
        const binary = atob(pdfBase64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
        blob = new Blob([bytes], { type: 'application/pdf' });
    } else {
        blob = await res.blob();
    }

    downloadBlob(blob, `${fileBase({ clientName, clientCase, fileSuffix })}.pdf`);
};
